import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";

export default function AboutPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    window.scrollTo(0, 0);
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Colorful%20Cameroonian%20open%20air%20food%20market%20Douala%20with%20local%20vendors%20cooking%20traditional%20dishes%20vibrant%20atmosphere%20warm%20morning%20sunlight%20authentic%20African%20marketplace%20scene%20wide%20shot%20cinematic%20editorial%20photography&width=1600&height=800&seq=about-hero-v2&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70"></div>
          </div>
          <div className="relative w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 text-background-50 text-xs font-semibold uppercase tracking-wider mb-6">
                {t("nav.about")}
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold text-background-50 mb-6 leading-tight tracking-tight">
                {t("about.title")}
              </h1>
              <p className="text-lg md:text-xl text-background-200/90 leading-relaxed max-w-2xl mx-auto">
                {t("about.subtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative -mt-14 pb-0">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
            <div className="bg-background-50 rounded-2xl shadow-lg border border-background-200 p-6 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { number: "500+", label: "Vendeurs locaux" },
                  { number: "200+", label: "Livreurs moto" },
                  { number: "2", label: "Villes couvertes" },
                  { number: "100%", label: "Mobile Money" },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
                      {stat.number}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Notre Histoire */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Images */}
              <div className="relative">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl overflow-hidden h-44 md:h-52">
                    <img
                      src="https://readdy.ai/api/search-image?query=Cameroonian%20woman%20street%20food%20vendor%20cooking%20traditional%20ndole%20dish%20over%20open%20fire%20colorful%20market%20background%20warm%20natural%20lighting%20authentic%20candid%20editorial%20photography&width=500&height=500&seq=about-story1-v2&orientation=squarish"
                      alt="Food vendor"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden h-44 md:h-52 mt-6">
                    <img
                      src="https://readdy.ai/api/search-image?query=Motorcycle%20taxi%20riders%20lined%20up%20Douala%20street%20waiting%20for%20passengers%20colorful%20helmets%20warm%20afternoon%20sunlight%20authentic%20urban%20Cameroon%20scene%20editorial%20photography&width=500&height=500&seq=about-story2-v2&orientation=squarish"
                      alt="Riders"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden h-44 md:h-52">
                    <img
                      src="https://readdy.ai/api/search-image?query=Traditional%20Cameroonian%20dishes%20assortment%20poulet%20DG%20ndole%20plantains%20achu%20displayed%20on%20colorful%20African%20fabric%20table%20warm%20lighting%20appetizing%20food%20editorial%20photography&width=500&height=500&seq=about-story3-v2&orientation=squarish"
                      alt="Local dishes"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden h-44 md:h-52 mt-6">
                    <img
                      src="https://readdy.ai/api/search-image?query=Bustling%20Douala%20street%20market%20scene%20colorful%20spices%20fresh%20produce%20vibrant%20textiles%20local%20shoppers%20warm%20golden%20sunlight%20authentic%20African%20atmosphere%20wide%20editorial%20photography&width=500&height=500&seq=about-story4-v2&orientation=squarish"
                      alt="Market"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className="space-y-6">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold uppercase tracking-wider mb-2">
                  Notre Histoire
                </span>
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                  {t("about.description1")}
                </p>
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                  {t("about.description2")}
                </p>
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                  {t("about.description3")}
                </p>
                <div className="pt-4">
                  <div className="inline-flex items-center gap-3 px-5 py-3 bg-primary-100/60 rounded-full">
                    <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-double-quotes-l text-background-50 text-xs"></i>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-primary-800 italic">
                      {t("footer.tagline")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Notre Mission */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-semibold uppercase tracking-wider mb-5">
                Notre Mission
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
                Ce qui nous anime
              </h2>
              <div className="w-20 h-1 bg-primary-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { icon: "ri-store-2-line", title: t("about.mission1_title"), desc: t("about.mission1_desc") },
                { icon: "ri-motorbike-line", title: t("about.mission2_title"), desc: t("about.mission2_desc") },
                { icon: "ri-restaurant-2-line", title: t("about.mission3_title"), desc: t("about.mission3_desc") },
                { icon: "ri-hand-heart-line", title: t("about.mission4_title"), desc: t("about.mission4_desc") },
              ].map((item, index) => (
                <div key={index} className="group bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200 hover:shadow-md transition-all duration-300 hover:border-primary-200">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-200 transition-colors group-hover:scale-110 duration-300">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-primary-500">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/50 to-transparent"></div>
          <div className="relative w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-5">
              Pret a rejoindre la communaute Mayouk ?
            </h2>
            <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-xl mx-auto">
              Faites partie du mouvement qui ramene la livraison de repas locaux dans chaque quartier du Cameroun.
            </p>
            <Link
              to="/waitlist"
              className="inline-flex items-center gap-2 px-8 py-4 bg-background-50 text-primary-600 rounded-full text-sm font-semibold hover:bg-background-100 transition-all hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              {t("hero.cta_waitlist")}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}